/** buyScore v2 core — scoringVersion "v2". MACD excluded from score. */

import { calcRsiMidlineScore } from "./rsiScoring.js";

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function obvSlope10(closes, volumes) {
  if (!closes?.length || !volumes?.length || closes.length < 12) return null;
  let obv = 0;
  const arr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) obv -= volumes[i] || 0;
    arr.push(obv);
  }
  const recent = arr.slice(0, 10).reduce((s, v) => s + v, 0) / Math.min(10, arr.length);
  const prior = arr.slice(10, 20).reduce((s, v) => s + v, 0) / Math.min(10, Math.max(1, arr.length - 10));
  if (recent > prior * 1.02) return 1;
  if (recent < prior * 0.98) return -1;
  return 0;
}

function bbSqueezeScore(closes, bb, maxAbs = 2) {
  if (!bb?.isSqueeze) return 0;
  return Math.min(maxAbs, 1);
}

function donchianBreakScore(closes, highs, lows, rvol) {
  if (!closes?.length || !highs?.length || highs.length < 21) return 0;
  const upper = Math.max(...highs.slice(1, 21));
  const lower = Math.min(...lows.slice(1, 21));
  const ratio = rvol?.ratio ?? 0;
  if (ratio < 1.3) return 0;
  if (closes[0] > upper) return 2;
  if (closes[0] < lower) return -2;
  return 0;
}

function sectorRotation20(spy20, sector20) {
  if (spy20 == null || sector20 == null) return 0;
  const spread = sector20 - spy20;
  if (spread > 2) return 1;
  if (spread < -2) return -1;
  return 0;
}

function rsBlendedSpread(stock20, stock63, spy20, spy63) {
  let spread = null;
  if (stock20 != null && spy20 != null && stock63 != null && spy63 != null) {
    spread = (stock20 - spy20 + (stock63 - spy63)) / 2;
  } else if (stock63 != null && spy63 != null) {
    spread = stock63 - spy63;
  } else if (stock20 != null && spy20 != null) {
    spread = stock20 - spy20;
  }
  if (spread == null) return 0;
  if (spread > 5) return 2;
  if (spread > 1) return 1;
  if (spread >= -1) return 0;
  if (spread < -5) return -2;
  return -1;
}

/**
 * Core score before weekly/tide/add-on bundles (caller applies caps & multipliers).
 */
export function computeCoreBuyScoreV2(ctx) {
  const {
    price,
    closes,
    highs,
    lows,
    volumes,
    sm50,
    sm200,
    cmf,
    bb,
    rvol,
    phaseContext,
    stockReturn20,
    stockReturn63,
    spyReturn20,
    spyReturn63,
    pureCrypto,
  } = ctx;

  const ema50 = sm50 ?? (closes ? emaLatest(closes, 50) : null);
  const ema200 = sm200 ?? (closes ? emaLatest(closes, 200) : null);

  let trend = 0;
  if (price != null && ema50 != null) trend += price > ema50 ? 2 : -2;
  if (ema50 != null && ema200 != null) trend += ema50 > ema200 ? 2 : -2;
  if (price != null && ema200 != null) trend += price > ema200 ? 1 : -1;

  const spy20 = phaseContext?.spy20dPct ?? spyReturn20 ?? null;
  const spy63 = phaseContext?.spy63dPct ?? spyReturn63 ?? null;
  const sector20 = phaseContext?.sector20dPct ?? null;
  const rsCore = pureCrypto
    ? 0
    : rsBlendedSpread(stockReturn20, stockReturn63, spy20, spy63);

  let money = 0;
  if (cmf != null) {
    if (cmf > 0.1) money += 2;
    else if (cmf > 0.05) money += 1;
    else if (cmf < -0.1) money -= 2;
    else if (cmf < -0.05) money -= 1;
  }
  const obvSl = obvSlope10(closes, volumes);
  if (obvSl === 1) money += 1;
  else if (obvSl === -1) money -= 1;

  const momentum = closes?.length ? calcRsiMidlineScore(closes) : 0;
  const breakout = donchianBreakScore(closes, highs, lows, rvol);
  const squeeze = bbSqueezeScore(closes, bb, 2);
  const sector = sectorRotation20(spy20, sector20);

  const core =
    trend + rsCore + money + momentum + breakout + squeeze + sector;

  let buyScore = Math.round(core * 2) / 2;

  const belowBoth =
    price != null &&
    ema50 != null &&
    ema200 != null &&
    price < ema50 &&
    price < ema200;
  const aboveBoth =
    price != null &&
    ema50 != null &&
    ema200 != null &&
    price > ema50 &&
    price > ema200;
  if (belowBoth && buyScore > 2) buyScore = 2;
  if (aboveBoth && buyScore < -2) buyScore = -2;

  return {
    buyScoreCore: buyScore,
    breakdown: { trend, rsCore, money, momentum, breakout, squeeze, sector },
    ema50,
    ema200,
    belowBoth,
    aboveBoth,
  };
}

/** Cap combined setup/confluence/measurement/phase/dcb bonuses to ±3. */
export function applyAddonCap(baseScore, addonTotal) {
  const capped = Math.max(-3, Math.min(3, addonTotal));
  return Math.round((baseScore + capped) * 2) / 2;
}

export function clampScore(s) {
  return Math.max(-15, Math.min(15, Math.round(s * 2) / 2));
}
